/**
 * KycDateTimePicker - Web 日期选择器组件
 *
 * @description
 * 日期选择器组件的 Web 端实现，专为 KYC 流程设计。
 *
 * @usage
 * ```tsx
 * // 基础用法
 * <KycDateTimePicker
 *   label="出生日期"
 *   value={birthDate}
 *   placeholder="请选择出生日期"
 *   dateFormat="YYYY-MM-DD"
 *   onValueChange={(date) => setBirthDate(date)}
 *   error={errors.birthDate}
 *   tip="请确保日期准确无误"
 * />
 * ```
 */

import React, { useCallback } from "react";
import { StyleSheet } from "../../platformComponents";
import { AsFormItemLayout } from "../AsFormItemLayout";
import { CommonDateTimePicker } from "../../common";
import { fontSize, padding } from "../../styles/common";
import type { IKycDateTimePickerProps } from "@adgm/cross-models";
import { KycLabelElement } from "../KycLabelElement";

/**
 * KycDateTimePicker Web 端日期选择器组件, 专为 KYC 流程设计。
 *
 * 相比 Native 版本，Web 版本直接使用底层日期选择器组件，无需额外的模态框层。
 *
 * @param props 组件属性配置
 * @returns 日期选择器组件 JSX 元素
 */
export const KycDateTimePicker = React.memo((props: IKycDateTimePickerProps) => {
  const {
    style,
    label,
    error,
    tip,
    hideErrorSpace,
    value,
    placeholder,
    onValueChange,
    dateFormat,
    disabled = false,
    sequenceNumber,
    horizontalLayout,
    required,
    ...restProps
  } = props;

  /**
   * 处理日期确认选择
   *
   * 当用户在日期选择器中选择日期时触发此回调。
   * 使用 useCallback 优化性能，避免不必要的重新渲染。
   *
   * @param date 用户选择的日期字符串
   */
  const handleDateConfirm = useCallback(
    (date: string) => {
      // 调用外部传入的回调函数，通知父组件日期变化
      onValueChange?.(date);
    },
    [onValueChange],
  );

  return (
    <AsFormItemLayout
      label={label}
      error={error}
      tip={tip}
      hideErrorSpace={hideErrorSpace}
      horizontalLayout={horizontalLayout}
      style={style}
      required={required}
      labelElement={
        <KycLabelElement
          label={label}
          sequenceNumber={sequenceNumber}
          horizontalLayout={horizontalLayout}
          required={required}
        />
      }
      {...restProps}>
      <CommonDateTimePicker
        style={styles.datePicker}
        value={value}
        placeholder={placeholder}
        dateFormat={dateFormat}
        disabled={disabled}
        onChange={handleDateConfirm}
      />
    </AsFormItemLayout>
  );
});
const styles = StyleSheet.create({
  datePicker: {
    flex: 1,
    fontSize: fontSize.f14,
    fontFamily: "Common-Regular",
    paddingVertical: padding.p16,
    paddingHorizontal: padding.p20,
    border: 0,
    padding: padding.p14,
  },
});
